<template>
  <BaseReport
    ref="baseReport"
    :loading="loading"
    :reportTitle="reportTitle"
    :reportSubtitle="dateRangeLabel"
    :columns="6"
    @mounted="onMounted"
  >
    <template #controls>
      <div class="report-controls">
        <AnalyticsDateRangePicker
          :modelValue="{ startDate: rangeSelection.startDate, endDate: rangeSelection.endDate }"
          @update:modelValue="handleDateRangeChange"
        />
        <AnalyticsPeriodPicker
          v-model="rangeSelection.intervalType"
          @update:modelValue="handlePeriodChange"
        />
        <UserSelectInput
          :modelValue="selectedCounselorId"
          @update:modelValue="handleCounselorChange"
          placeholder="Select counselor..."
          class="counselor-select"
        />
      </div>
    </template>

    <template #header v-if="data?.counselor">
      <div class="report-header-with-icon">
        <IconCircle
          :size="48"
          bgColor="var(--icon-user-bg)"
          color="var(--icon-user-color)"
          :initials="getCounselorInitials(data.counselor.counselorName)"
        />
        <div class="report-header-text">
          <h2>{{ data.counselor.counselorName }}</h2>
          <p>All metrics reflect tickets created in the selected date range. Click charts to drill down to individual tickets.</p>
        </div>
      </div>
    </template>

    <template #content>
      <!-- Empty state when no counselor selected -->
      <div v-if="!selectedCounselorId" class="empty-state">
        <p>Select a counselor to view their detailed performance</p>
      </div>

      <!-- Report content when counselor is selected -->
      <template v-else-if="data">
        <!-- Row 1: Summary KPIs -->
        <CardKpi
          class="cell cell-chart"
          title="Total Tickets"
          :value="totalTicketsKpi.value"
          format="number"
          :description="totalTicketsKpi.description"
        />
        <CardKpi
          class="cell cell-chart"
          title="Total Sales"
          :value="totalSalesKpi.value"
          format="currency"
          :description="totalSalesKpi.description"
        />
        <CardKpi
          class="cell cell-chart"
          title="Total Collected"
          :value="totalCollectedKpi.value"
          format="currency"
          :description="totalCollectedKpi.description"
        />

        <!-- Row 2: Distribution Charts -->
        <CardBarChart
          class="cell cell-half"
          title="Tickets by Price"
          subtitle="NP vs EP by price range"
          :data="priceBucketsChartData"
          :options="priceBucketsChartOptions"
          variant="stacked"
          :horizontal="true"
          :height="300"
          @bar-click="handlePriceBucketsBarClick"
        />
        <CardBarChart
          class="cell cell-half"
          title="Top Products"
          subtitle="Revenue by product, NP vs EP"
          :data="topProductsChartData"
          :options="topProductsChartOptions"
          variant="stacked"
          :horizontal="true"
          :height="300"
          @bar-click="handleTopProductsBarClick"
        />

        <!-- Row 3: Sales by Patient Type -->
        <CardLineChart
          class="cell cell-full-width"
          title="Sales by Patient Type"
          subtitle="NP vs EP over time"
          variant="stacked"
          :data="patientTypeTrendData"
          :options="patientTypeLineChartOptions"
          :formatTooltipValue="formatCurrencyValue"
          :height="300"
          @point-click="handlePatientTypeChartClick"
        />

        <!-- Row 4: Sales by Product -->
        <CardLineChart
          class="cell cell-full-width"
          title="Sales by Product"
          subtitle="Per product over time"
          variant="stacked"
          :data="productTrendData"
          :options="productLineChartOptions"
          :formatTooltipValue="formatCurrencyValue"
          :height="300"
          @point-click="handleProductChartClick"
        />

        <!-- Row 4: Product Sales Table -->
        <CardTable
          class="cell cell-full-width cell-data-table"
          title="Product Sales by Period"
          subtitle="Revenue per product per period"
          :columns="productSalesTableColumns"
          :rows="productSalesTableRows"
          variant="summary"
          :summaryRow="productSalesTableSummary"
          :stickyFirstColumn="true"
        />
      </template>
    </template>
  </BaseReport>
</template>

<script>
import BaseReport from '@/components/common/BaseReport.vue';
import CardBarChart from '@/components/common/CardBarChart.vue';
import CardLineChart from '@/components/common/CardLineChart.vue';
import CardKpi from '@/components/common/CardKpi.vue';
import CardTable from '@/components/common/CardTable.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import AnalyticsDateRangePicker from '@/components/inputs/AnalyticsDateRangePicker.vue';
import AnalyticsPeriodPicker from '@/components/inputs/AnalyticsPeriodPicker.vue';
import UserSelectInput from '@/components/inputs/UserSelectInput.vue';
import * as api from '@/api';
import { alertService } from '@/libs/alert';
import { serializeFilters } from '@/utils/filterSerializer';
import { formatDateShort } from '@/utils/formatters';

export default {
  name: 'CounselorDetailsReport',
  components: {
    BaseReport,
    CardBarChart,
    CardLineChart,
    CardKpi,
    CardTable,
    IconCircle,
    AnalyticsDateRangePicker,
    AnalyticsPeriodPicker,
    UserSelectInput
  },
  emits: ['loading'],
  data() {
    return {
      loading: true,
      rangeSelection: {
        startDate: null,
        endDate: null,
        intervalType: 'weekly'
      },
      selectedCounselorId: null,
      data: null
    };
  },
  computed: {
    reportTitle() {
      return 'Counselor Details';
    },
    dateRangeLabel() {
      if (!this.rangeSelection.startDate || !this.rangeSelection.endDate) {
        return '';
      }
      return `${formatDateShort(this.rangeSelection.startDate)} - ${formatDateShort(this.rangeSelection.endDate)}`;
    },
    chartHeight() {
      return 300;
    },

    // Color schemes
    patientTypeColors() {
      return {
        newPatient: '#818cf8',      // indigo-400 (lighter)
        existingPatient: '#4338ca'  // indigo-700 (darker)
      };
    },

    // Total Tickets KPI
    totalTicketsKpi() {
      const priceBuckets = this.data?.summary?.priceBuckets || [];
      const npTickets = priceBuckets.reduce((sum, b) => sum + b.npTickets, 0);
      const epTickets = priceBuckets.reduce((sum, b) => sum + b.epTickets, 0);
      const total = npTickets + epTickets;

      return {
        value: total,
        description: `${npTickets} new patient and ${epTickets} existing patient tickets in this period`
      };
    },

    // Total Sales KPI
    totalSalesKpi() {
      const sales = this.data?.summary?.sales;
      if (!sales) return { value: 0, description: '' };

      const npTotal = (sales.npPayInFullCents || 0) + (sales.npPaymentPlanCents || 0);
      const epTotal = (sales.epPayInFullCents || 0) + (sales.epPaymentPlanCents || 0);

      return {
        value: sales.totalCents / 100,
        description: `${this.formatCurrencyShort(npTotal / 100)} from new patients, ${this.formatCurrencyShort(epTotal / 100)} from existing patients`
      };
    },

    // Total Collected KPI
    totalCollectedKpi() {
      const totalCents = this.data?.summary?.totalCollectedCents || 0;
      const npCents = this.data?.summary?.npCollectedCents || 0;
      const epCents = this.data?.summary?.epCollectedCents || 0;

      return {
        value: totalCents / 100,
        description: `${this.formatCurrencyShort(npCents / 100)} from new patients, ${this.formatCurrencyShort(epCents / 100)} from existing patients`
      };
    },

    // Price Buckets Bar Chart Data
    priceBucketsChartData() {
      if (!this.data?.summary?.priceBuckets) return null;

      const buckets = this.data.summary.priceBuckets;
      // Filter out empty buckets for cleaner display
      const nonEmptyBuckets = buckets.filter(b => b.npTickets > 0 || b.epTickets > 0);

      if (nonEmptyBuckets.length === 0) return null;

      return {
        labels: nonEmptyBuckets.map(b => b.label),
        datasets: [
          {
            label: 'Existing Patient',
            data: nonEmptyBuckets.map(b => b.epTickets),
            color: this.patientTypeColors.existingPatient
          },
          {
            label: 'New Patient',
            data: nonEmptyBuckets.map(b => b.npTickets),
            color: this.patientTypeColors.newPatient
          }
        ]
      };
    },
    priceBucketsChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];

            let total = 0;
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;

            params.forEach(param => {
              const value = param.value || 0;
              total += value;
              const color = param.color;
              html += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
              html += `<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${color}; margin-right: 8px;"></span>`;
              html += `<span style="flex: 1;">${param.seriesName}:</span>`;
              html += `<span style="font-weight: 600; margin-left: 12px;">${value} tickets</span>`;
              html += `</div>`;
            });

            html += `<div style="border-top: 1px solid rgba(255,255,255,0.2); margin-top: 8px; padding-top: 8px; font-weight: 600;">`;
            html += `<span>Total:</span>`;
            html += `<span style="float: right; margin-left: 12px;">${total} tickets</span>`;
            html += `</div>`;

            return html;
          }
        }
      };
    },

    // Top Products Bar Chart Data
    topProductsChartData() {
      if (!this.data?.summary?.topProducts) return null;

      const products = this.data.summary.topProducts;
      if (products.length === 0) return null;

      // Take top 7 products, aggregate rest into "Other"
      const topProducts = products.slice(0, 7);
      const otherProducts = products.slice(7);

      // Build data array with top products
      const chartData = [...topProducts];

      // Add "Other" row if there are more products
      if (otherProducts.length > 0) {
        const otherEpSales = otherProducts.reduce((sum, p) => sum + (p.existingPatientSalesCents || 0), 0);
        const otherNpSales = otherProducts.reduce((sum, p) => sum + (p.newPatientSalesCents || 0), 0);
        if (otherEpSales > 0 || otherNpSales > 0) {
          chartData.push({
            productName: 'Other',
            existingPatientSalesCents: otherEpSales,
            newPatientSalesCents: otherNpSales
          });
        }
      }

      // Reverse so highest is at top in horizontal bar
      const reversed = chartData.reverse();

      return {
        labels: reversed.map(p => p.productName),
        datasets: [
          {
            label: 'Existing Patient',
            data: reversed.map(p => p.existingPatientSalesCents / 100),
            color: this.patientTypeColors.existingPatient
          },
          {
            label: 'New Patient',
            data: reversed.map(p => p.newPatientSalesCents / 100),
            color: this.patientTypeColors.newPatient
          }
        ]
      };
    },
    topProductsChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];

            let total = 0;
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;

            params.forEach(param => {
              const value = param.value || 0;
              total += value;
              const color = param.color;
              html += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
              html += `<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${color}; margin-right: 8px;"></span>`;
              html += `<span style="flex: 1;">${param.seriesName}:</span>`;
              html += `<span style="font-weight: 600; margin-left: 12px;">${this.formatCurrencyValue(value)}</span>`;
              html += `</div>`;
            });

            html += `<div style="border-top: 1px solid rgba(255,255,255,0.2); margin-top: 8px; padding-top: 8px; font-weight: 600;">`;
            html += `<span>Total:</span>`;
            html += `<span style="float: right; margin-left: 12px;">${this.formatCurrencyValue(total)}</span>`;
            html += `</div>`;

            return html;
          }
        }
      };
    },

    // Patient Type Trend Data
    patientTypeTrendData() {
      if (!this.data?.salesByPatientType) return null;

      const { periods, existingPatientSales, newPatientSales } = this.data.salesByPatientType;

      return {
        labels: periods,
        datasets: [
          {
            label: 'Existing Patient',
            data: existingPatientSales.map(v => v / 100),
            color: this.patientTypeColors.existingPatient
          },
          {
            label: 'New Patient',
            data: newPatientSales.map(v => v / 100),
            color: this.patientTypeColors.newPatient
          }
        ]
      };
    },

    // Product Trend Data
    productTrendData() {
      if (!this.data?.salesByProduct?.products) return null;

      const { periods, products } = this.data.salesByProduct;

      // Sort products by total sales (descending)
      const sortedProducts = [...products].sort((a, b) => b.totalSalesCents - a.totalSalesCents);

      // Take top 7, group rest under "Other"
      const topProducts = sortedProducts.slice(0, 7);
      const otherProducts = sortedProducts.slice(7);

      // Calculate "Other" totals per period
      let otherData = null;
      if (otherProducts.length > 0) {
        const otherSalesPerPeriod = periods.map((_, periodIndex) => {
          return otherProducts.reduce((sum, p) => sum + (p.salesPerPeriod[periodIndex] || 0), 0);
        });
        const otherTotal = otherSalesPerPeriod.reduce((sum, v) => sum + v, 0);
        if (otherTotal > 0) {
          otherData = {
            label: 'Other',
            data: otherSalesPerPeriod.map(v => v / 100),
            color: '#e0e7ff' // indigo-100 (lightest)
          };
        }
      }

      // Reverse so highest is on top in stacked chart
      const colors = this.generateIndigoShades(topProducts.length);
      const datasets = topProducts.reverse().map((product, index) => ({
        label: product.productName,
        data: product.salesPerPeriod.map(v => v / 100),
        color: colors[topProducts.length - 1 - index]
      }));

      // Add "Other" at the bottom of the stack
      if (otherData) {
        datasets.unshift(otherData);
      }

      return {
        labels: periods,
        datasets
      };
    },

    patientTypeLineChartOptions() {
      return {
        yAxis: {
          axisLabel: {
            formatter: (value) => this.formatCurrencyShort(value)
          }
        }
      };
    },
    productLineChartOptions() {
      return {
        yAxis: {
          axisLabel: {
            formatter: (value) => this.formatCurrencyShort(value)
          }
        }
      };
    },

    // Product Sales Table Data
    productSalesTableColumns() {
      if (!this.data?.salesByProduct?.periods) return [];

      const periods = this.data.salesByProduct.periods;
      return [
        { key: 'product', label: 'Product' },
        ...periods.map((period, index) => ({
          key: `period_${index}`,
          label: period,
          format: 'currency'
        })),
        { key: 'total', label: 'Total', format: 'currency' }
      ];
    },
    productSalesTableRows() {
      if (!this.data?.salesByProduct?.products) return [];

      const { products } = this.data.salesByProduct;

      // Sort by total sales descending
      const sortedProducts = [...products].sort((a, b) => b.totalSalesCents - a.totalSalesCents);

      // Take top 7, group rest under "Other"
      const topProducts = sortedProducts.slice(0, 7);
      const otherProducts = sortedProducts.slice(7);

      const rows = topProducts.map(product => {
        const row = {
          product: product.productName,
          total: product.totalSalesCents / 100
        };
        product.salesPerPeriod.forEach((sales, index) => {
          row[`period_${index}`] = sales / 100;
        });
        return row;
      });

      // Add "Other" row if needed
      if (otherProducts.length > 0) {
        const otherRow = { product: 'Other', total: 0 };
        const periodCount = otherProducts[0]?.salesPerPeriod?.length || 0;
        for (let i = 0; i < periodCount; i++) {
          const periodTotal = otherProducts.reduce((sum, p) => sum + (p.salesPerPeriod[i] || 0), 0);
          otherRow[`period_${i}`] = periodTotal / 100;
          otherRow.total += periodTotal / 100;
        }
        if (otherRow.total > 0) {
          rows.push(otherRow);
        }
      }

      return rows;
    },
    productSalesTableSummary() {
      if (!this.productSalesTableRows.length) return null;

      const summary = { product: 'Total', total: 0 };
      const periodCount = this.data?.salesByProduct?.periods?.length || 0;

      for (let i = 0; i < periodCount; i++) {
        summary[`period_${i}`] = this.productSalesTableRows.reduce((sum, row) => sum + (row[`period_${i}`] || 0), 0);
      }
      summary.total = this.productSalesTableRows.reduce((sum, row) => sum + (row.total || 0), 0);

      return summary;
    }
  },
  methods: {
    generateIndigoShades(count) {
      // Generate different shades of indigo from light to dark (matches FinancialsReport)
      const indigoShades = [
        '#c7d2fe', // indigo-200
        '#a5b4fc', // indigo-300
        '#818cf8', // indigo-400
        '#6366f1', // indigo-500
        '#4f46e5', // indigo-600
        '#4338ca', // indigo-700
        '#3730a3', // indigo-800
        '#312e81'  // indigo-900
      ];

      if (count > indigoShades.length) {
        const result = [];
        for (let i = 0; i < count; i++) {
          result.push(indigoShades[i % indigoShades.length]);
        }
        return result;
      }

      return indigoShades.slice(0, count);
    },

    async onMounted() {
      // Initialize from URL query params if present
      const query = this.$route.query;
      if (query.startDate) {
        this.rangeSelection.startDate = query.startDate;
      }
      if (query.endDate) {
        this.rangeSelection.endDate = query.endDate;
      }
      if (query.intervalType) {
        this.rangeSelection.intervalType = query.intervalType;
      }
      if (query.counselorId) {
        this.selectedCounselorId = parseInt(query.counselorId, 10);
      }

      // Load data if all required params are present
      if (this.rangeSelection.startDate && this.rangeSelection.endDate && this.selectedCounselorId) {
        await this.loadData();
      }

      this.$emit('loading', false);
      this.loading = false;
    },

    handleDateRangeChange(dateRange) {
      this.rangeSelection.startDate = dateRange.startDate;
      this.rangeSelection.endDate = dateRange.endDate;
      if (this.rangeSelection.startDate && this.rangeSelection.endDate && this.selectedCounselorId) {
        this.loadData();
      }
    },

    handlePeriodChange() {
      if (this.rangeSelection.startDate && this.rangeSelection.endDate && this.selectedCounselorId) {
        this.loadData();
      }
    },

    handleCounselorChange(counselorId) {
      this.selectedCounselorId = counselorId;
      if (this.rangeSelection.startDate && this.rangeSelection.endDate && counselorId) {
        this.loadData();
      } else {
        this.data = null;
      }
    },

    async loadData() {
      if (!this.rangeSelection.startDate || !this.rangeSelection.endDate || !this.selectedCounselorId) {
        return;
      }

      this.loading = true;

      try {
        const result = await api.fetchCounselorDetails({
          startDate: this.rangeSelection.startDate,
          endDate: this.rangeSelection.endDate,
          intervalType: this.rangeSelection.intervalType,
          counselorId: this.selectedCounselorId
        });

        this.data = result;
      } catch (err) {
        console.error('Failed to load counselor details:', err);
        alertService.error('Failed to load counselor details: ' + err.message);
        this.data = null;
      } finally {
        this.loading = false;
      }
    },

    handlePatientTypeChartClick(event) {
      const { dataIndex, seriesName } = event;
      const periodDates = this.data?.salesByPatientType?.periodDates;
      if (!periodDates || !periodDates[dataIndex]) return;

      const period = periodDates[dataIndex];

      const filters = [
        {
          field: 'dateTicketStart',
          operator: 'between',
          from: period.start,
          to: period.end
        }
      ];

      // Add counselorId filter
      if (this.selectedCounselorId) {
        filters.push({
          field: 'counselorId',
          operator: 'is',
          value: this.selectedCounselorId
        });
      }

      // Add ticketType filter based on which series was clicked
      if (seriesName === 'New Patient') {
        filters.push({
          field: 'ticketType',
          operator: 'is',
          value: 'new_patient'
        });
      } else if (seriesName === 'Existing Patient') {
        filters.push({
          field: 'ticketType',
          operator: 'is',
          value: 'current_patient'
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    handleProductChartClick(event) {
      const { dataIndex, seriesName } = event;
      const periodDates = this.data?.salesByProduct?.periodDates;
      if (!periodDates || !periodDates[dataIndex]) return;

      const period = periodDates[dataIndex];
      const product = this.data.salesByProduct.products.find(p => p.productName === seriesName);

      const filters = [
        {
          field: 'dateTicketStart',
          operator: 'between',
          from: period.start,
          to: period.end
        }
      ];

      // Add counselorId filter
      if (this.selectedCounselorId) {
        filters.push({
          field: 'counselorId',
          operator: 'is',
          value: this.selectedCounselorId
        });
      }

      if (product) {
        filters.push({
          field: 'productIds',
          operator: 'any',
          values: [product.productId]
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    handlePriceBucketsBarClick(event) {
      const { name } = event;

      const filters = [];

      // Add date range filter if available
      if (this.rangeSelection.startDate && this.rangeSelection.endDate) {
        filters.push({
          field: 'dateTicketStart',
          operator: 'between',
          from: this.rangeSelection.startDate,
          to: this.rangeSelection.endDate
        });
      }

      // Add counselorId filter
      if (this.selectedCounselorId) {
        filters.push({
          field: 'counselorId',
          operator: 'is',
          value: this.selectedCounselorId
        });
      }

      // Add totalPrice between filter based on the price bucket clicked
      const bucket = this.data?.summary?.priceBuckets?.find(b => b.label === name);
      if (bucket) {
        filters.push({
          field: 'totalPrice',
          operator: 'between',
          from: bucket.minCents,
          to: bucket.maxCents
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    handleTopProductsBarClick(event) {
      const { name } = event;
      if (!name) return;

      // Find the product by name from the top products list
      const product = this.data?.summary?.topProducts?.find(p => p.productName === name);

      const filters = [];

      // Add date range filter if available
      if (this.rangeSelection.startDate && this.rangeSelection.endDate) {
        filters.push({
          field: 'dateTicketStart',
          operator: 'between',
          from: this.rangeSelection.startDate,
          to: this.rangeSelection.endDate
        });
      }

      // Add counselorId filter
      if (this.selectedCounselorId) {
        filters.push({
          field: 'counselorId',
          operator: 'is',
          value: this.selectedCounselorId
        });
      }

      // Add product filter if found
      if (product) {
        filters.push({
          field: 'productIds',
          operator: 'any',
          values: [product.productId]
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    navigateToTickets(startDate, endDate) {
      const filters = [
        {
          field: 'dateTicketStart',
          operator: 'between',
          from: startDate,
          to: endDate
        }
      ];

      // Add counselorId filter
      if (this.selectedCounselorId) {
        filters.push({
          field: 'counselorId',
          operator: 'is',
          value: this.selectedCounselorId
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    formatCurrencyValue(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value);
    },

    formatCurrencyShort(value) {
      if (value >= 1000000) {
        return '$' + (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return '$' + (value / 1000).toFixed(0) + 'K';
      }
      return '$' + value.toFixed(2);
    },

    async generatePDF() {
      const counselorName = this.data?.counselor?.counselorName || '';
      const title = counselorName ? `Counselor Details — ${counselorName}` : 'Counselor Details';
      this.$refs.baseReport.generateReportPDF({
        title,
        subtitle: this.dateRangeLabel,
        kpis: [
          { label: 'Total Tickets', value: this.totalTicketsKpi.value, format: 'number' },
          { label: 'Total Sales', value: this.totalSalesKpi.value, format: 'currency' },
          { label: 'Total Collected', value: this.totalCollectedKpi.value, format: 'currency' }
        ],
        columns: this.productSalesTableColumns,
        rows: this.productSalesTableRows,
        summaryRow: this.productSalesTableSummary
      });
    },

    getCounselorInitials(name) {
      if (!name) return '';
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
  }
};
</script>

<style scoped>
.report-header-with-icon {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.report-header-text h2 {
  margin: 0 0 4px 0;
}

.report-header-text p {
  margin: 0;
}

.report-controls {
  display: flex;
  gap: 12px;
  align-items: center;
}

.counselor-select {
  width: 200px;
  flex-shrink: 0;
}

.empty-state {
  grid-column: 1 / -1;
  padding: 64px;
  text-align: center;
  color: #6b7280;
  font-size: 1rem;
}

.cell-chart {
  grid-column: span 2;
}

.cell-half {
  grid-column: span 3;
}

.cell-full-width {
  grid-column: span 6;
}

.cell-data-table :deep(.data-table th),
.cell-data-table :deep(.data-table td) {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
}

@media (max-width: 768px) {
  .cell-chart,
  .cell-half,
  .cell-full-width {
    grid-column: span 6;
  }
}
</style>
